import React, { useCallback } from 'react';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import i18nService from 'src/services/i18n.service';
import { dialogContent, dialogTitle, modal } from 'src/components/ConfirmationModal/ConfirmationModal.styles';
import { FavoriteListItem } from 'src/components/Subheader/Favorites/Favorites.types';
import { Dialog, DialogTitle, DialogContent, CircularProgress } from '@material-ui/core';
import { TFunction } from 'i18next';

export enum DeleteType {
  favorite,
  configuratorView,
  configuratorTab,
}

interface DeleteConfirmationItem {
  favorite?: FavoriteListItem;
  view?: string;
  tab?: string;
}

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: (event?: React.MouseEvent<HTMLElement>) => void;
  onDelete: (favorite?: FavoriteListItem) => void;
  deleteType: DeleteType;
  item: DeleteConfirmationItem;
  isSaving?: boolean;
}

const getItemName = (type: DeleteType, item: DeleteConfirmationItem): string => {
  switch (type) {
    case DeleteType.favorite:
      return item.favorite?.name || '';
    case DeleteType.configuratorView:
      return item.view || '';
    case DeleteType.configuratorTab:
      return item.tab || '';
    default:
      return '';
  }
};

const getDeleteTypeString = (type: DeleteType, t: TFunction) => {
  switch (type) {
    case DeleteType.favorite:
      return t('favorite', { postProcess: 'capitalize' });
    case DeleteType.configuratorView:
      return 'View';
    case DeleteType.configuratorTab:
      return 'Tab';
    default:
      return '';
  }
};

export const DeleteConfirmationModal = (props: DeleteConfirmationModalProps) => {
  const { t } = useTranslation(undefined, { i18n: i18nService });
  const { onDelete, onClose, deleteType, item, isSaving } = props;
  const { favorite } = item;
  const deleteTypeString = getDeleteTypeString(deleteType, t);
  const handleDelete = useCallback(() => {
    if (!isNil(favorite) && deleteType === DeleteType.favorite) {
      onDelete(favorite);
    } else {
      onDelete();
    }
  }, [deleteType, favorite, onDelete]);

  return (
    <Dialog open={props.isOpen} onClose={() => onClose} maxWidth={'md'} classes={{ root: modal }}>
      <DialogTitle classes={{ root: dialogTitle }}>
        <div>
          <span className="left-container">
            <i className="fas fa-trash icon fa-sm" />
            Delete {deleteTypeString}
          </span>
          <span className="right-container">
            <i className="far fa-times" onClick={onClose} />
          </span>
        </div>
      </DialogTitle>
      <DialogContent classes={{ root: dialogContent }}>
        <p className="instructions">
          Delete {deleteTypeString}: {getItemName(deleteType, item)}
        </p>
        <footer>
          <button onClick={handleDelete} className="apply" disabled={isSaving}>
            {isSaving ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <i className="far fa-check" />
                Delete
              </>
            )}
          </button>
          <button onClick={onClose} className="reset" disabled={isSaving}>
            <i className="fas fa-ban" />
            Cancel
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
